import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { privateAxios } from "../../axiosInstances";
import eventsQueryKeys from "../../query-keys/events.query-keys";

interface CreateEventPasswordData {
  password: string;
}

interface UpdateEventPasswordData {
  currentPassword: string;
  newPassword: string;
}

interface DeleteEventPasswordData {
  isPublic?: boolean;
}

const createEventPassword = async (
  eventId: string,
  data: CreateEventPasswordData
) => {
  try {
    const response = await privateAxios.post(
      `/events/${eventId}/password`,
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message:
          error.response?.data.message ||
          "Erreur lors de la création du mot de passe",
      };
    }
    throw error;
  }
};

export const useCreateEventPasswordMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEventPasswordData) =>
      createEventPassword(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.getComplete(eventId),
      });
    },
  });
};

const updateEventPassword = async (
  eventId: string,
  data: UpdateEventPasswordData
) => {
  try {
    const response = await privateAxios.patch(
      `/events/${eventId}/password`,
      data
    );
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message:
          error.response?.data.message ||
          "Erreur lors de la modification du mot de passe",
      };
    }
    throw error;
  }
};

export const useUpdateEventPasswordMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateEventPasswordData) =>
      updateEventPassword(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.getComplete(eventId),
      });
    },
  });
};

const deleteEventPassword = async (
  eventId: string,
  data?: DeleteEventPasswordData
) => {
  try {
    const response = await privateAxios.delete(`/events/${eventId}/password`, {
      data,
    });
    return response.data;
  } catch (error) {
    if (isAxiosError(error)) {
      throw {
        ...error,
        message:
          error.response?.data.message ||
          "Erreur lors de la suppression du mot de passe",
      };
    }
    throw error;
  }
};

export const useDeleteEventPasswordMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data?: DeleteEventPasswordData) =>
      deleteEventPassword(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.get(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventsQueryKeys.getComplete(eventId),
      });
    },
  });
};
